import { resupplyConfigFor } from '@1delta/data-sdk'
import { ResupplyRegistryAbi, ResupplyPairAbi } from '@1delta/abis'
import { multicallRetryUniversal } from '@1delta/providers'
import { Call } from '../../../utils/multicall'

/**
 * User-data call build for Resupply. Positions are keyed by OWNER ADDRESS —
 * one position per user per pair, no ids and no sub-accounts.
 *
 * DISCOVERY-FIRST (the Teller / Frankencoin shape), for a reason that is not
 * obvious and cost a fork test to find:
 *
 *   **`getUserSnapshot` is not a `view`.** It calls `_syncUserRedemptions`,
 *   which mutates storage to settle the user's share of every communal
 *   redemption since they last touched the pair. That is exactly what makes
 *   the returned collateral correct — but it also makes the call EXPENSIVE,
 *   and issuing one per pair for the whole roster overruns the gas budget of a
 *   single Multicall3 `aggregate3`. Measured on a mainnet fork: a 63-call
 *   batch (21 pairs x 3) decoded only 40 calls. Worse, the shared multicall
 *   layer only retries when EVERY call in a request fails, so under
 *   `allowFailure: true` that partial out-of-gas is swallowed and the affected
 *   pairs read as "no position" — a user's position would silently vanish.
 *
 * So the snapshots run HERE, chunked, during the (async) build phase, and only
 * pairs where the user actually holds something get call slots in the shared
 * batch. A typical user has a position on one pair, so the batch the executor
 * runs is 2 calls, not 63.
 *
 * per ACTIVE pair (discovery order):
 *   getPairAccounting()               — totals WITH interest previewed
 *   collateral.convertToAssets(1e18)  — shares → underlying
 *
 * The snapshot values themselves are cached alongside the active-pair list and
 * consumed by the parser.
 */
export const RESUPPLY_CALLS_PER_PAIR = 2

const ONE = 10n ** 18n
const ROSTER_TTL_MS = 5 * 60_000
const DISCOVERY_TTL_MS = 60_000

/**
 * Snapshots per chunk. 30 decoded cleanly on a fork where 63 did not; 12 keeps
 * a wide margin and still covers the whole roster in two requests.
 */
const SNAPSHOT_CHUNK = 12

interface PairRosterEntry {
  pair: string
  collateral: string
}

interface PairRoster {
  pairs: PairRosterEntry[]
  at: number
}

export interface ResupplyActivePosition extends PairRosterEntry {
  borrowShares: bigint
  collateralShares: bigint
}

export interface ResupplyDiscovery {
  positions: ResupplyActivePosition[]
  at: number
}

const rosterCache = new Map<string, PairRoster>()
const discoveryCache = new Map<string, ResupplyDiscovery>()
const rosterKey = (chainId: string, lender: string) => `${chainId}:${lender}`
const discoveryKey = (chainId: string, lender: string, account: string) =>
  `${chainId}:${lender}:${account.toLowerCase()}`

/**
 * The parse phase needs the exact same active-pair list the build phase used —
 * exposed rather than re-discovered so a state change in between cannot desync
 * the slicer from the call list.
 */
export const getCachedResupplyDiscovery = (
  chainId: string,
  lender: string,
  account: string,
): ResupplyDiscovery | undefined =>
  discoveryCache.get(discoveryKey(chainId, lender, account))

/** Discover pairs + their collateral vaults, cached per chain+lender. */
const fetchRoster = async (
  chainId: string,
  lender: string,
  registry: string,
  allowlist?: string[] | null,
): Promise<PairRoster> => {
  const cached = rosterCache.get(rosterKey(chainId, lender))
  if (cached && Date.now() - cached.at < ROSTER_TTL_MS) return cached

  let addresses: string[] = []
  const [raw] = (await multicallRetryUniversal({
    chain: chainId,
    calls: [{ address: registry, name: 'getAllPairAddresses', params: [] }],
    abi: ResupplyRegistryAbi as any,
    allowFailure: true,
  })) as any[]
  if (Array.isArray(raw)) {
    addresses = raw.filter(
      (a: any) => typeof a === 'string' && a.startsWith('0x'),
    )
  }
  if (allowlist && allowlist.length > 0) {
    const allow = new Set(allowlist.map((p) => p.toLowerCase()))
    addresses = addresses.filter((p) => allow.has(p.toLowerCase()))
  }
  if (addresses.length === 0) {
    const empty = { pairs: [], at: Date.now() }
    rosterCache.set(rosterKey(chainId, lender), empty)
    return empty
  }

  const cols = (await multicallRetryUniversal({
    chain: chainId,
    calls: addresses.map((pair) => ({
      address: pair,
      name: 'collateral',
      params: [],
    })),
    abi: addresses.map(() => ResupplyPairAbi) as any,
    allowFailure: true,
  })) as any[]

  const pairs = addresses
    .map((pair, i) => ({ pair, collateral: cols[i] }))
    .filter(
      (p): p is PairRosterEntry =>
        typeof p.collateral === 'string' && p.collateral.startsWith('0x'),
    )

  const roster = { pairs, at: Date.now() }
  rosterCache.set(rosterKey(chainId, lender), roster)
  return roster
}

/**
 * Snapshot every pair for one account, in chunks, and keep the ones where the
 * user holds anything. A chunk that fails outright is skipped rather than
 * reported as empty — the difference between "no position" and "we could not
 * read" matters here, because the caller acts on the answer.
 */
const discoverPositions = async (
  chainId: string,
  pairs: PairRosterEntry[],
  account: string,
): Promise<ResupplyActivePosition[]> => {
  const active: ResupplyActivePosition[] = []
  for (let i = 0; i < pairs.length; i += SNAPSHOT_CHUNK) {
    const slice = pairs.slice(i, i + SNAPSHOT_CHUNK)
    let res: any[] = []
    try {
      res = (await multicallRetryUniversal({
        chain: chainId,
        calls: slice.map((p) => ({
          address: p.pair,
          name: 'getUserSnapshot',
          params: [account],
        })),
        abi: slice.map(() => ResupplyPairAbi) as any,
        allowFailure: true,
      })) as any[]
    } catch {
      continue
    }
    slice.forEach((p, j) => {
      const snap = res[j]
      if (!Array.isArray(snap)) return
      const borrowShares = typeof snap[0] === 'bigint' ? snap[0] : 0n
      const collateralShares = typeof snap[1] === 'bigint' ? snap[1] : 0n
      if (borrowShares === 0n && collateralShares === 0n) return
      active.push({ ...p, borrowShares, collateralShares })
    })
  }
  return active
}

export const buildResupplyUserCall = async (
  chainId: string,
  lender: string,
  account: string,
): Promise<Call[]> => {
  const cfg = resupplyConfigFor(lender, chainId)
  const key = discoveryKey(chainId, lender, account)
  const stash = (positions: ResupplyActivePosition[]) =>
    discoveryCache.set(key, { positions, at: Date.now() })

  if (!cfg?.registry) {
    stash([])
    return []
  }

  let roster: PairRoster
  try {
    roster = await fetchRoster(chainId, lender, cfg.registry, cfg.pairAllowlist)
  } catch {
    // Discovery failure ⇒ report nothing rather than an empty book. A stale
    // roster inside the TTL is still served by `fetchRoster` above.
    return []
  }
  if (roster.pairs.length === 0) {
    stash([])
    return []
  }

  const cachedDiscovery = discoveryCache.get(key)
  const positions =
    cachedDiscovery && Date.now() - cachedDiscovery.at < DISCOVERY_TTL_MS
      ? cachedDiscovery.positions
      : await discoverPositions(chainId, roster.pairs, account)

  stash(positions)
  if (positions.length === 0) return []

  return positions.flatMap(({ pair, collateral }) => [
    // Totals with interest previewed — the denominator for the share→amount
    // conversion the parser does locally.
    { address: pair, name: 'getPairAccounting', params: [] },
    { address: collateral, name: 'convertToAssets', params: [ONE] },
  ])
}

/** Test seam — drops the roster + discovery caches. */
export function __resetResupplyUserCaches(): void {
  rosterCache.clear()
  discoveryCache.clear()
}
